import { useMemo, ChangeEvent } from 'react';
import { Box, Text, Checkbox, Link } from '@nimbus-ds/components';
import { generatePath } from 'react-router';
import { CATALOG_SORT_PROD_MULTI_SELECT } from 'App/featuresFlags';
import { useHasTags } from 'App/hooks';
import { ImageItemStratus } from 'commons/components';
import { CATALOG_ROUTES } from 'domains/Catalog/catalogRoutes';
import { ProductHiddenAndWithoutStock } from 'domains/Catalog/Products/pages/components';
import useTranslationCatalog from 'domains/Catalog/useTranslationCatalog';

export interface ProductsBySectionItemProps {
  productId: string;
  name: string;
  position: number;
  imageUrl?: string;
  visible: boolean;
  stock: boolean;
  selected?: boolean;
  onChangeSelected?: (position: number, checked: boolean) => void;
  onMoveProductToUp?: (position: number) => void;
}

function ProductsBySectionItem({
  productId,
  imageUrl,
  name,
  position,
  visible,
  stock,
  selected,
  onChangeSelected,
  onMoveProductToUp,
}: Readonly<ProductsBySectionItemProps>) {
  const [hasMultiSelect] = useHasTags([CATALOG_SORT_PROD_MULTI_SELECT]);
  const t = useTranslationCatalog();

  const productPath = useMemo(
    () =>
      generatePath(CATALOG_ROUTES.editProduct, {
        id: productId,
      }),
    [productId],
  );

  const mustRenderCheckbox = selected !== undefined && hasMultiSelect;

  const gridTemplateColumns = mustRenderCheckbox ? '32px 32px auto' : '1fr';

  const handleChangeSelected = (e: ChangeEvent<HTMLInputElement>) => {
    onChangeSelected?.(position, e.target.checked);
  };

  return (
    <Box
      display="grid"
      alignItems="center"
      gridTemplateColumns={gridTemplateColumns}
      gap="2"
    >
      {mustRenderCheckbox && (
        <>
          <Checkbox
            name={`checkbox[${position}]`}
            aria-label={t('products.order.productsBySeccion.ariaLabel', {
              name,
            })}
            checked={selected}
            onChange={handleChangeSelected}
          />
          <Text>{position + 1}</Text>
        </>
      )}
      <ImageItemStratus
        id={productId}
        imageUrl={imageUrl}
        title={name}
        bold={false}
        to={productPath}
        trimTitle
        imageWidth="56px"
        rightContent={
          onMoveProductToUp &&
          !hasMultiSelect &&
          position > 0 && (
            <Link
              as="button"
              appearance="primary"
              onClick={() => onMoveProductToUp(position)}
            >
              {t('products.detail.moveProductToUp')}
            </Link>
          )
        }
      >
        <ProductHiddenAndWithoutStock
          productId={productId}
          isVisible={visible}
          hasStock={stock}
        />
      </ImageItemStratus>
    </Box>
  );
}

export default ProductsBySectionItem;
